import { lessonsInOrder, type Bundle, type Lesson } from "@otto/lehrgang";
import { cssVar } from "../../../shared/lib/cssVar.js";
import type { Progress } from "../../progress/lib/progress.js";
import { lockOf, openLessonKeys, type Gate } from "../lib/gate.js";
import { starsForLesson } from "../lib/lessonStars.js";
import styles from "./LessonList.module.css";
import frame from "../../../shared/components/shell.module.css";

/**
 * Wie viele Lektionen der Gastmodus hergibt.
 *
 * Eine PRODUKTGRENZE, keine Lehrgangsangabe – deshalb steht sie hier und nicht
 * im Bundle. Läge sie dort, könnte die Redaktion die Grenze des
 * Geschäftsmodells verschieben; genau das war beim Lizenzdatum schon einmal
 * möglich und ist mit Absicht abgestellt worden.
 */
export const GUEST_LESSONS = 3;

/**
 * Welche Lektionen jetzt offen sind – aus BEIDEN Grenzen.
 *
 * Sie sind verschiedener Art und werden deshalb getrennt gerechnet: der
 * Gastmodus ist eine Produktgrenze, das Gatter eine Unterrichtsentscheidung.
 * Zusammen heißt: eine Lektion ist offen, wenn keine der beiden sie zuhält.
 */
export function openLessons(
  bundle: Bundle,
  { hasCode, progress, gate }: { hasCode: boolean; progress: Progress; gate: Gate },
): readonly Lesson[] {
  const ordered = lessonsInOrder(bundle);
  const durchsGatter = openLessonKeys(bundle, progress, gate);
  const bisGast = hasCode ? ordered : ordered.slice(0, GUEST_LESSONS);
  return bisGast.filter((lesson) => durchsGatter.has(lesson.key));
}

/**
 * Der Satz auf einer zugehaltenen Kachel.
 *
 * Er nennt ein Ziel, kein Verbot: „Erst Lektion 3 zu Ende üben“ sagt, was zu tun
 * ist. „Gesperrt“ sagt nur, dass etwas nicht geht.
 */
function reasonFor(lock: NonNullable<ReturnType<typeof lockOf>>): string {
  if (lock.stars === null) return `Erst Lektion ${lock.after.number} zu Ende üben`;
  const sterne = lock.stars === 1 ? "1 Stern" : `${lock.stars} Sterne`;
  return `Erst ${sterne} in Lektion ${lock.after.number}`;
}

/**
 * Die Übersicht über den Lehrgang: eine Kachel je Lektion in
 * Lehrgangsreihenfolge, jede mit ihren Sternen.
 *
 * Die Sterne zeigen immer drei Plätze, auch leere – ein Kind sieht von Anfang
 * an, dass es drei zu holen gibt. So war es im Prototyp.
 *
 * Im Gastmodus bleiben die gesperrten Kacheln stehen, ausgegraut. Sie zu
 * verstecken wäre bequemer, aber dann sähe niemand, dass es weitergeht – und
 * die Lehrkraft, die den Lehrgang beurteilen soll, sähe drei Lektionen und
 * hielte das für alles.
 *
 * Dasselbe gilt für das Gatter der Klasse – mit einem Unterschied: dort steht
 * der Grund AUF der Kachel. Für den Gastmodus gibt es einen Satz unter der
 * Liste, weil er für alle gesperrten derselbe ist; beim Gatter nennt jede
 * Kachel eine andere Lektion, und ein Satz für alle wäre für keine richtig.
 */
export function LessonList({
  bundle,
  progress,
  hasCode,
  gate,
  onChoose,
}: {
  bundle: Bundle;
  progress: Progress;
  hasCode: boolean;
  gate: Gate;
  onChoose: (lesson: Lesson) => void;
}) {
  const open = new Set(
    openLessons(bundle, { hasCode, progress, gate }).map((l) => l.key),
  );
  return (
    <>
      <header className={frame.header}>
        <div className={frame.brand}>Dein Tipp-Lehrgang</div>
        {/* Zahl aus dem Bundle, nicht im Text: "sechs Lektionen" stand hier noch,
            als der Lehrgang längst acht hatte. */}
        <h1 className={frame.title}>
          {lessonsInOrder(bundle).length} Lektionen, Schritt für Schritt
        </h1>
        <p className={frame.subtitle}>
          Wähle deine Lektion. Genauigkeit zählt, nicht Tempo.
        </p>
      </header>

      <div className={frame.card}>
        <div className={styles.grid}>
          {lessonsInOrder(bundle).map((lesson) => {
            const stars = starsForLesson(lesson, progress, bundle.starThresholds);
            const locked = !open.has(lesson.key);
            // Nur der Grund aus dem Gatter. Ist die Kachel bloß im Gastmodus zu,
            // steht der Satz unter der Liste - er gilt dann für alle gleich.
            const lock = lockOf(lesson, bundle, progress, gate);
            return (
              <button
                key={lesson.key}
                type="button"
                // Zwei Arten, zu zu sein, und sie sehen verschieden aus: das
                // Gatter nennt einen Grund und muss lesbar bleiben, der
                // Gastmodus meint bei allen dasselbe. Beides zugleich kann
                // nicht eintreten - ohne Code gilt kein Gatter.
                className={`${styles.lesson} ${lock ? styles.gated : locked ? styles.locked : ""}`}
                style={cssVar({ "--lesson-color": lesson.color })}
                disabled={locked}
                onClick={() => onChoose(lesson)}
              >
                <span className={styles.number}>{lesson.number}</span>
                <span className={styles.lessonTitle}>{lesson.title}</span>
                <span className={styles.lessonSubtitle}>{lesson.subtitle}</span>
                <span className={styles.stars} aria-label={`${stars ?? 0} von 3 Sternen`}>
                  <span className={styles.starsFilled}>{"★".repeat(stars ?? 0)}</span>
                  <span className={styles.starsEmpty}>
                    {"★".repeat(3 - (stars ?? 0))}
                  </span>
                </span>
                {lock && <span className={styles.reason}>{reasonFor(lock)}</span>}
              </button>
            );
          })}
        </div>
      </div>

      <p className={frame.footnote}>
        Am besten: eine Lektion pro Woche, jede in zwei Übungseinheiten. Genauigkeit
        zählt, nicht Tempo.
      </p>
    </>
  );
}
